(() => {
  if (customElements.get('kr-product-addon')) return;

  class KrProductAddon extends HTMLElement {
    connectedCallback() {
      this.form = this.closest('.kr-pdp__form');
      this.root = this.closest('[data-kr-pdp]');
      this.check = this.querySelector('[data-kr-addon-check]');
      this.variant = this.querySelector('[data-kr-addon-variant]');
      this.status = this.querySelector('[data-kr-addon-status]');
      this.message = this.querySelector('[data-kr-addon-message]');
      this.mainVariant = this.root?.querySelector('[data-kr-variant]');
      this.quantity = this.root?.querySelector('[data-kr-quantity]');
      this.addButton = this.root?.querySelector('[data-kr-add]');
      if (!this.form || !this.root || !this.check || !this.variant || !this.mainVariant || !this.quantity || !this.addButton || !this.status || !this.message) return;

      try {
        this.addUrl = new URL(this.dataset.addUrl, location.href);
        this.cartUrl = new URL(this.dataset.cartUrl, location.href);
        if (this.addUrl.origin !== location.origin || this.cartUrl.origin !== location.origin || !/\/cart\/add\.js$/.test(this.addUrl.pathname) || !/\/cart\/?$/.test(this.cartUrl.pathname)) return;
      } catch (_) { return; }

      this.listeners?.abort();
      this.listeners = new AbortController();
      const options = { signal: this.listeners.signal };
      this.variant.addEventListener('change', () => this.syncVariant(), options);
      if (this.dataset.matchSize === 'true') this.mainVariant.addEventListener('change', () => this.syncSize(), options);
      this.root.addEventListener('submit', event => this.onSubmit(event), options);
      if (!this.pending) {
        if (this.dataset.matchSize === 'true') this.syncSize();
        else this.syncVariant();
      }
      this.hidden = false;
    }

    disconnectedCallback() {
      this.listeners?.abort();
    }

    syncSize() {
      if (this.pending) return;
      const mainIndex = Number(this.dataset.mainSizeIndex);
      const addonIndex = Number(this.dataset.addonSizeIndex);
      if (mainIndex < 0 || addonIndex < 0) return this.syncVariant();
      try {
        const mainValues = JSON.parse(this.mainVariant.selectedOptions[0]?.dataset.options || '[]');
        const currentValues = JSON.parse(this.variant.selectedOptions[0]?.dataset.options || '[]');
        const size = mainValues[mainIndex];
        if (!size) return this.syncVariant();
        const options = [...this.variant.options];
        for (const option of options) {
          const values = JSON.parse(option.dataset.options || '[]');
          const matches = values[addonIndex] === size;
          option.hidden = !matches;
          option.disabled = !matches || option.dataset.available !== 'true';
        }
        const candidates = options.filter(option => !option.disabled);
        const sameColor = candidates.find(option => {
          const values = JSON.parse(option.dataset.options || '[]');
          return values.every((value, index) => index === addonIndex || value === currentValues[index]);
        });
        const next = sameColor || candidates[0];
        if (next) this.variant.value = next.value;
      } catch (_) {
        this.check.checked = false;
        this.check.disabled = true;
        return;
      }
      this.syncVariant();
    }

    syncVariant() {
      if (this.pending) return;
      const option = this.variant.selectedOptions[0];
      const available = Boolean(option && !option.disabled && option.dataset.available === 'true');
      this.check.disabled = !available;
      if (!available) this.check.checked = false;
      const price = this.querySelector('[data-kr-addon-price]');
      if (price && option) price.textContent = option.dataset.price || '';
      const originalPrice = this.querySelector('[data-kr-addon-original-price]');
      if (originalPrice && option) originalPrice.textContent = option.dataset.originalPrice || '';
      const image = this.querySelector('[data-kr-addon-image]');
      if (image && option?.dataset.image) {
        try {
          const url = new URL(option.dataset.image, location.href);
          if (url.protocol === 'https:' || (url.protocol === 'http:' && url.origin === location.origin)) {
            image.removeAttribute('srcset');
            image.src = url.href;
            image.alt = option.dataset.imageAlt || '';
          }
        } catch (_) {}
      }
    }

    showError(description) {
      const label = this.dataset.errorLabel || 'We could not confirm that both items were added. Please check your cart before trying again.';
      this.message.textContent = description ? `${label} ${description}` : label;
      this.status.hidden = false;
    }

    setPending() {
      this.pending = true;
      this.status.hidden = true;
      this.message.textContent = '';
      const controls = new Set([
        ...this.form.querySelectorAll('button, input, select, textarea'),
        ...this.root.querySelectorAll('[data-kr-option], [data-kr-swatch], [data-kr-sticky-add], [data-kr-sticky-option], [data-kr-sticky-quantity], [data-kr-sticky-minus], [data-kr-sticky-plus], [data-kr-variant], [data-kr-quantity], [data-kr-minus], [data-kr-plus]'),
      ]);
      this.controlStates = [...controls].filter(control => 'disabled' in control).map(control => ({ control, disabled: control.disabled }));
      this.root.dataset.krPurchasePending = 'true';
      this.controlStates.forEach(({ control }) => { control.disabled = true; });
      this.buttonStates = [...this.root.querySelectorAll('[data-kr-add], [data-kr-sticky-add]')].map(button => ({ button, text: button.textContent, busy: button.getAttribute('aria-busy') }));
      this.buttonStates.forEach(({ button }) => {
        button.textContent = this.dataset.pendingLabel || 'Adding…';
        button.setAttribute('aria-busy', 'true');
      });
      this.previousBusy = this.getAttribute('aria-busy');
      this.setAttribute('aria-busy', 'true');
      this.root.dispatchEvent(new CustomEvent('kr:purchase-pending'));
    }

    clearPending() {
      this.controlStates?.forEach(({ control, disabled }) => { control.disabled = disabled; });
      this.buttonStates?.forEach(({ button, text, busy }) => {
        button.textContent = text;
        if (busy === null) button.removeAttribute('aria-busy');
        else button.setAttribute('aria-busy', busy);
      });
      if (this.previousBusy === null) this.removeAttribute('aria-busy');
      else this.setAttribute('aria-busy', this.previousBusy);
      this.pending = false;
      this.root.dataset.krPurchasePending = 'false';
      this.root.dispatchEvent(new CustomEvent('kr:purchase-pending'));
    }

    async onSubmit(event) {
      if (event.target !== this.form) return;
      if (this.pending) {
        event.preventDefault();
        this.controlStates?.forEach(({ control }) => { control.disabled = true; });
        return;
      }
      if (!this.check.checked || event.defaultPrevented) return;
      event.preventDefault();

      const mainOption = this.mainVariant.selectedOptions[0];
      const addonOption = this.variant.selectedOptions[0];
      const mainId = Number(this.mainVariant.value);
      const addonId = Number(this.variant.value);
      const quantity = Number(this.quantity.value);
      const positiveInteger = value => Number.isSafeInteger(value) && value > 0;
      if (this.root.dataset.live !== 'true' || this.addButton.disabled || !mainOption || mainOption.disabled || mainOption.dataset.available !== 'true' || !addonOption || addonOption.disabled || addonOption.dataset.available !== 'true' || !positiveInteger(mainId) || !positiveInteger(addonId)) {
        this.showError();
        return;
      }
      if (!positiveInteger(quantity) || !this.quantity.checkValidity() || !this.form.checkValidity()) {
        this.form.reportValidity();
        return;
      }

      this.setPending();
      let navigating = false;
      const requestController = new AbortController();
      const timeout = setTimeout(() => requestController.abort(), 20000);
      try {
        const response = await fetch(this.addUrl.href, {
          method: 'POST',
          credentials: 'same-origin',
          signal: requestController.signal,
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: mainId, quantity }, { id: addonId, quantity: 1 }] }),
        });
        const result = await response.json();
        if (!response.ok) {
          const description = typeof result?.description === 'string' ? result.description : '';
          this.showError(description);
          return;
        }
        location.assign(this.cartUrl.href);
        navigating = true;
      } catch (_) {
        this.showError();
      } finally {
        clearTimeout(timeout);
        if (!navigating) this.clearPending();
      }
    }
  }

  customElements.define('kr-product-addon', KrProductAddon);
})();
